import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { isNativeNotWeb } from '@/utils/platform-check';
import { shouldBlockTracking } from '@/utils/mixpanel';
import { getAppVersion } from '@/utils/app-version';
import { scrubBeforeSend, scrubRumBeforeSend } from '@/utils/log-scrubber';

let isRumInitialized = false;
let isLogsInitialized = false;
let isSessionReplayRecording = false; // Track session replay recording state

// Fix E — named logger via the public SDK API. The prior implementation
// attached a custom `source: 'console'` context, but `source` is one of
// Datadog's reserved attributes and gets remapped by the intake pipeline,
// so the forwarder tag is lost. Using `datadogLogs.createLogger(name)` sets
// logger.name correctly through documented API surface with no reserved
// attribute collision.
// https://docs.datadoghq.com/logs/log_configuration/attributes_naming_convention/
// https://docs.datadoghq.com/logs/log_collection/javascript/#named-logger
let consoleForwarder = null;

const stringifyArgs = args =>
  args
    .map(arg => {
      if (typeof arg === 'object') {
        try {
          return JSON.stringify(arg);
        } catch {
          return String(arg);
        }
      }
      return String(arg);
    })
    .join(' ');

// Which console calls reach Datadog Logs. Only warnings by default: info and
// debug were ~85% of this app's log volume and nobody reads them there. The
// Capgo updater alone wrote ~1,300 debug/info lines per phone per OTA update
// (two per file in the bundle) and ~14 on every app open, about 3.4M lines a
// week in Oct 2026. They still print to the device console. Set
// VUE_APP_DATADOG_CONSOLE_LEVEL to 'info' or 'debug' to forward more.
//
// console.error is not forwarded here: forwardErrorsToLogs already sends it,
// through the same beforeSend scrubber, and forwarding it here too sent every
// error twice.
const CONSOLE_LEVEL_RANK = { debug: 0, info: 1, warn: 2 };
const consoleForwardRank = () =>
  CONSOLE_LEVEL_RANK[process.env.VUE_APP_DATADOG_CONSOLE_LEVEL] ?? CONSOLE_LEVEL_RANK.warn;

export const shouldForwardConsole = level => CONSOLE_LEVEL_RANK[level] >= consoleForwardRank();

/**
 * Forward console.warn (and, if configured, info/log/debug) to Datadog Logs.
 */
function setupConsoleLogInterceptor() {
  if (!isLogsInitialized) {
    return;
  }

  consoleForwarder = datadogLogs.createLogger('console-forwarder');
  const originalConsoleError = console.error.bind(console);

  // console.log is forwarded at info level, as before.
  const intercept = (method, level) => {
    if (!shouldForwardConsole(level)) return;
    const original = console[method].bind(console);
    console[method] = function (...args) {
      original(...args);
      try {
        consoleForwarder.log(stringifyArgs(args), {}, level);
      } catch (error) {
        originalConsoleError(`[Fix-E Datadog] Failed to forward ${method}:`, error);
      }
    };
  };

  intercept('warn', 'warn');
  intercept('info', 'info');
  intercept('log', 'info');
  intercept('debug', 'debug');
}

/**
 * Initialize Datadog Logs
 */
export function initDatadogLogs() {
  if (isLogsInitialized) {
    console.log('[Datadog Logs] Already initialized');
    return;
  }

  if (!process.env.VUE_APP_DATADOG_CLIENT_TOKEN) {
    console.warn(
      '[Datadog Logs] Missing VUE_APP_DATADOG_CLIENT_TOKEN. Logs will not be initialized.',
    );
    return;
  }

  try {
    datadogLogs.init({
      clientToken: process.env.VUE_APP_DATADOG_CLIENT_TOKEN,
      site: 'datadoghq.com',
      service: process.env.VUE_APP_DATADOG_SERVICE || 'hogc-one-mobile',
      env: process.env.VUE_APP_NODE_ENV || 'development',
      forwardErrorsToLogs: true,
      sampleRate: 100,
      // Strips tokens and personal data from every event, on both paths into
      // Datadog Logs: the console interceptor above and forwardErrorsToLogs.
      beforeSend: scrubBeforeSend,
    });

    isLogsInitialized = true;
    console.log('[Datadog Logs] Initialized successfully');

    // Setup console.log interceptor after initialization
    setupConsoleLogInterceptor();
  } catch (error) {
    console.error('[Datadog Logs] Failed to initialize:', error);
  }
}

/**
 * Initialize Datadog RUM
 * Async because we resolve the installed app version from Capacitor before init.
 */
export async function initDatadogRum() {
  // Check if already initialized
  if (isRumInitialized) {
    console.log('[Datadog RUM] Already initialized');
    return;
  }

  // Check if required environment variables are present
  if (!process.env.VUE_APP_DATADOG_RUM_APP_ID || !process.env.VUE_APP_DATADOG_CLIENT_TOKEN) {
    console.warn(
      '[Datadog RUM] Missing required environment variables. RUM will not be initialized.',
    );
    return;
  }

  // `version` is already the bundle when one is running — RUM's version should
  // describe the code that threw, and the source maps that decode it, not the
  // shell it loaded in. The binary version is kept as context below so an error
  // can still be traced to the install it happened on.
  const { version, nativeVersion, bundleVersion } = await getAppVersion();

  // Log configuration for debugging
  console.log('[Datadog RUM] Initialization config:', {
    applicationId: process.env.VUE_APP_DATADOG_RUM_APP_ID ? '***' : 'MISSING',
    clientToken: process.env.VUE_APP_DATADOG_CLIENT_TOKEN ? '***' : 'MISSING',
    site: 'datadoghq.com',
    service: process.env.VUE_APP_DATADOG_SERVICE || 'hogc-one-mobile',
    env: process.env.VUE_APP_NODE_ENV || 'development',
    version,
    nativeVersion,
  });

  try {
    datadogRum.init({
      applicationId: process.env.VUE_APP_DATADOG_RUM_APP_ID,
      clientToken: process.env.VUE_APP_DATADOG_CLIENT_TOKEN,
      site: 'datadoghq.com',
      service: process.env.VUE_APP_DATADOG_SERVICE || 'hogc-one-mobile',
      env: process.env.VUE_APP_NODE_ENV || 'development',
      version,
      sessionSampleRate: 100, // 100% of sessions
      sessionReplaySampleRate: 100, // 100% of sessions for replay
      startSessionReplayRecordingManually: true, // Manual control of session replay
      sessionPersistence: 'local-storage', // Use localStorage for session persistence
      allowedTracingUrls: [process.env.VUE_APP_API_URL, process.env.VUE_APP_GIVING_API_V2_URL],
      trackResources: true,
      trackLongTasks: true,
      trackInteractions: true,
      defaultPrivacyLevel: 'mask-user-input', // Mask sensitive input fields
      // Strip URL query strings (prefilled form iframes carry member PII there)
      // and scrub error text, as Datadog Logs already does.
      beforeSend: scrubRumBeforeSend,
    });

    isRumInitialized = true;
    console.log('[Datadog RUM] Initialized successfully');

    // Keep the binary version queryable even when `version` is a bundle, so a
    // bundle-specific error can still be split by the native build it landed on.
    try {
      datadogRum.setGlobalContextProperty('native_version', nativeVersion);
      datadogRum.setGlobalContextProperty('ota_bundle_version', bundleVersion || null);
    } catch (contextError) {
      console.warn('[Datadog RUM] Could not tag version context:', contextError);
    }

    // Start session replay recording immediately to capture splash screen and login process
    // Use nextTick to ensure DOM is ready, but start as early as possible
    if (typeof window !== 'undefined') {
      // Start recording immediately - no delay to capture splash screen
      startSessionReplayRecording();

      // Verify RUM is actually initialized and working (non-blocking)
      setTimeout(() => {
        try {
          // Check if datadogRum methods are available
          const hasMethods = {
            addAction: typeof datadogRum.addAction === 'function',
            addError: typeof datadogRum.addError === 'function',
            setUser: typeof datadogRum.setUser === 'function',
            startSessionReplayRecording:
              typeof datadogRum.startSessionReplayRecording === 'function',
          };
          console.log('[Datadog RUM] Methods available:', hasMethods);

          // Try to get session replay link (verifies RUM is working)
          try {
            const replayLink = datadogRum.getSessionReplayLink();
            console.log('[Datadog RUM] Session replay link available:', !!replayLink);
          } catch (verifyError) {
            console.warn(
              '[Datadog RUM] Session replay link check failed (this is normal if no session yet):',
              verifyError.message,
            );
          }

          // Add a test action to verify data is being sent
          addDatadogRumAction('rum_initialized', {
            timestamp: new Date().toISOString(),
            page: typeof window !== 'undefined' ? window.location.pathname : 'unknown',
          });
          console.log('[Datadog RUM] Test action sent - check Datadog dashboard');
        } catch (testError) {
          console.error('[Datadog RUM] Verification failed:', testError);
        }
      }, 100); // Reduced delay for verification only
    }
  } catch (error) {
    console.error('[Datadog RUM] Failed to initialize:', error);
    console.error('[Datadog RUM] Error details:', {
      message: error?.message,
      stack: error?.stack,
      name: error?.name,
    });
  }
}

/**
 * Initialize both Datadog Logs and RUM
 */
export async function initDatadog() {
  initDatadogLogs();
  await initDatadogRum();
}

/**
 * Set user context for Datadog RUM
 * @param {Object} userInfo - User information
 * @param {string} userInfo.id - User ID
 * @param {string} userInfo.name - User name
 * @param {string} userInfo.email - User email
 * @param {Object} userInfo.additionalInfo - Additional user properties
 */
export function setDatadogRumUser(userInfo) {
  if (!isRumInitialized) {
    console.warn('[Datadog RUM] Cannot set user - RUM not initialized');
    return;
  }

  try {
    datadogRum.setUser({
      id: userInfo.id,
      name: userInfo.name,
      email: userInfo.email,
      ...userInfo.additionalInfo,
    });
    console.log('[Datadog RUM] User context set:', userInfo.id);
  } catch (error) {
    console.error('[Datadog RUM] Failed to set user:', error);
  }
}

/**
 * Clear user context (e.g., on logout)
 */
export function clearDatadogRumUser() {
  if (!isRumInitialized) {
    return;
  }

  try {
    datadogRum.clearUser();
    console.log('[Datadog RUM] User context cleared');
  } catch (error) {
    console.error('[Datadog RUM] Failed to clear user:', error);
  }
}

/**
 * Add custom action/event to RUM
 * @param {string} name - Action name
 * @param {Object} context - Additional context
 */
export function addDatadogRumAction(name, context = {}) {
  if (!isRumInitialized) {
    console.warn('[Datadog RUM] Cannot add action - RUM not initialized:', name);
    return;
  }

  try {
    datadogRum.addAction(name, context);
    console.log('[Datadog RUM] Action added:', name, context);
  } catch (error) {
    console.error('[Datadog RUM] Failed to add action:', error);
  }
}

/**
 * Add error to RUM
 * @param {Error} error - Error object
 * @param {Object} context - Additional context
 */
export function addDatadogRumError(error, context = {}) {
  if (!isRumInitialized) {
    return;
  }

  try {
    datadogRum.addError(error, context);
  } catch (error) {
    console.error('[Datadog RUM] Failed to add error:', error);
  }
}

/**
 * Stop RUM (e.g., when user denies tracking)
 */
export function stopDatadogRum() {
  if (!isRumInitialized) {
    return;
  }

  try {
    datadogRum.stopSession();
    isRumInitialized = false;
    console.log('[Datadog RUM] Stopped');
  } catch (error) {
    console.error('[Datadog RUM] Failed to stop:', error);
  }
}

/**
 * Check if RUM is initialized
 */
export function isDatadogRumInitialized() {
  return isRumInitialized;
}

/**
 * Check if Logs is initialized
 */
export function isDatadogLogsInitialized() {
  return isLogsInitialized;
}

/**
 * Get RUM status and diagnostics
 * Useful for troubleshooting why data might not be appearing in Datadog
 */
export function getDatadogRumStatus() {
  const isNative = isNativeNotWeb();

  if (!isRumInitialized) {
    return {
      initialized: false,
      message: 'RUM not initialized',
      isNative,
    };
  }

  try {
    const status = {
      initialized: true,
      sessionReplayRecording: isSessionReplayRecording,
      hasDatadogRum: typeof datadogRum !== 'undefined',
      isNative,
      config: {
        applicationId: process.env.VUE_APP_DATADOG_RUM_APP_ID ? 'SET' : 'MISSING',
        clientToken: process.env.VUE_APP_DATADOG_CLIENT_TOKEN ? 'SET' : 'MISSING',
        site: 'datadoghq.com',
        service: process.env.VUE_APP_DATADOG_SERVICE || 'hogc-one-mobile',
        env: process.env.VUE_APP_NODE_ENV || 'development',
        sessionPersistence: 'local-storage',
      },
    };

    // Try to get session replay link (verifies RUM is working)
    try {
      const replayLink = datadogRum.getSessionReplayLink();
      status.sessionReplayAvailable = !!replayLink;
    } catch (e) {
      status.sessionReplayAvailable = false;
      status.sessionReplayError = e.message;
    }

    return status;
  } catch (error) {
    return {
      initialized: true,
      error: error.message,
      isNative,
    };
  }
}

/**
 * Start session replay recording
 * Only starts if not already recording
 */
export function startSessionReplayRecording() {
  if (!isRumInitialized) {
    console.warn('[Datadog RUM] Cannot start session replay - RUM not initialized');
    return;
  }

  // Skip if already recording
  if (isSessionReplayRecording) {
    return;
  }

  try {
    datadogRum.startSessionReplayRecording();
    isSessionReplayRecording = true;
    console.log('[Datadog RUM] Session replay recording started');
  } catch (error) {
    console.error('[Datadog RUM] Failed to start session replay:', error);
  }
}

/**
 * Stop session replay recording
 * Only stops if currently recording
 */
export function stopSessionReplayRecording() {
  if (!isRumInitialized) {
    return;
  }

  // Skip if not recording
  if (!isSessionReplayRecording) {
    return;
  }

  try {
    datadogRum.stopSessionReplayRecording();
    isSessionReplayRecording = false;
    console.log('[Datadog RUM] Session replay recording stopped');
  } catch (error) {
    console.error('[Datadog RUM] Failed to stop session replay:', error);
  }
}

/**
 * Handle route change for session replay control
 * Stops recording for sensitive routes, starts for others
 * Uses shouldBlockTracking to maintain consistency with other tracking services
 * @param {string} routePath - The route path to check
 */
export function handleRouteChangeForSessionReplay(routePath) {
  if (!isRumInitialized) {
    return;
  }

  // Use shouldBlockTracking to check if route is sensitive
  // This ensures consistency with Mixpanel blocking logic
  const isSensitiveRoute = shouldBlockTracking(routePath);

  if (isSensitiveRoute) {
    stopSessionReplayRecording();
  } else {
    // Ensure recording is started for other pages
    startSessionReplayRecording();
  }
}
